import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { IonSearchbar, ModalController, NavParams } from '@ionic/angular/standalone';
import { IonSelectCustomEvent, SelectChangeEventDetail } from '@ionic/core';
import { TranslateService } from '@ngx-translate/core';
import { fieldSorter } from '@scandium-oy/ngx-scandium';
import { BehaviorSubject } from 'rxjs';
import { filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { SiteArea } from 'src/app/models/site-area.model';
import { Site, SiteBlueprint } from 'src/app/models/site.model';
import { AreasService } from 'src/app/services/areas.service';
import { AppAreaMapComponent } from 'src/app/sites/edit-site/floors/area-map.component';
import { ListRoomsComponent } from 'src/app/sites/site/components/rooms/components/list-rooms/list-rooms.component';
import { getLeaf, setAreaParents } from 'src/app/utility/kanban';
import { defaultRoom } from 'src/app/utility/room';
import { MainDialogComponent } from '../main-dialog/main-dialog.component';
import { NoResultsComponent } from '../no-results/no-results.component';
import { SegmentListComponent } from '../segment-list/segment-list.component';

@Component({
  selector: 'app-select-area-dialog',
  templateUrl: './select-area.dialog.html',
  styleUrls: ['./select-area.dialog.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    MainDialogComponent,
    SegmentListComponent,
    NoResultsComponent,
    AppAreaMapComponent,
    ListRoomsComponent,
  ],
})
export class SelectAreaDialogComponent {
  private areasService = inject(AreasService);
  private navParams = inject(NavParams);
  private translate = inject(TranslateService);

  private defaultRoom: SiteArea;
  preSelected$ = new BehaviorSubject<SiteArea[]>([]);
  private filter$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private onlyResidences = signal(false);

  selected = signal<{ id?: string; siteArea?: SiteArea }[]>([]);

  isMultiple = signal(false);
  isMultipleRooms = signal(false);
  clearButtonS = signal(false);
  segmentValue = signal<string>('list');
  blueprints = this.navParams.get<Site>('site').floors;
  imageElement = signal<HTMLImageElement>(null);
  selectedBlueprint = signal<SiteBlueprint>(null);

  siteS = signal<Site>(null);

  private areas$ = toObservable(this.siteS).pipe(
    filter((site) => site != null),
    switchMap((site) => this.areasService.getMyAreas(site.guid)),
    tap((areas) => {
      this.defaultRoom = areas?.find((it) => it.name === defaultRoom);
    }),
    map((areas) => areas?.filter((a) => a.name !== defaultRoom)?.sort(fieldSorter(['name'])) ?? []),
    tap((areas) => areas.map((a) => setAreaParents(a))),
    shareReplay(1),
  );

  readonly segmentButtons = [
    {
      value: 'list',
      name: this.translate.instant('item.list'),
      show: true,
    },
    {
      value: 'map',
      name: this.translate.instant('site.blueprint'),
      show: this.blueprints?.length > 0,
    },
  ];

  constructor(
    private _modal: ModalController,
  ) {
    const site = this.navParams.get<Site>('site');
    this.siteS.set(site);
    this.isMultiple.set(this.navParams.get('multiple') ?? false);
    this.isMultipleRooms.set(this.navParams.get('multipleRooms') ?? false);
    this.clearButtonS.set(this.navParams.get('clearButton') ?? false);
    this.onlyResidences.set(this.navParams.get('onlyResidences') ?? false);
    const preSelected = this.navParams.get<SiteArea[]>('areas');
    if (preSelected) {
      const value = preSelected.map((it) => ({ id: it.guid, siteArea: it }));
      this.selected.set(value);
      this.preSelected$.next(preSelected);
    }
  }

  private filterAreas(areas: SiteArea[], filterValue: string) {
    const ret: SiteArea[] = [];
    areas.map((area) => {
      const clone = Object.assign({}, area);
      if (clone.children.length > 0) {
        clone.children = this.filterAreas(clone.children, filterValue);
      }
      if (clone.name.toLowerCase().includes(filterValue.toLowerCase()) || clone.children.length > 0) {
        ret.push(clone);
      }
    });
    return ret;
  }

  private setSelected(area: SiteArea, selected: { siteArea?: SiteArea }[], root?: string) {
    selected.map((it) => {
      const isId = area.id === it.siteArea.id;
      if (isId) {
        area.selected = true;
      } else if (area.children.length > 0) {
        area.children.map((c) => this.setSelected(c, selected, root ?? area.guid));
      }
    }).filter((it) => it);
  }

  segmentChanged(value: string) {
    if (value) {
      this.segmentValue.set(value);
    }
  }

  clear() {
    this._modal.dismiss({ clear: true });
  }

  onFilter(input: IonSearchbar) {
    const filterValue = input.value;
    this.filter$.next(filterValue);
  }

  selectFiltered(areas: SiteArea[]) {
    const leafs = areas.map((area) => {
      const items = getLeaf(area);
      return items.map((it) => ({ root: area, area: it }));
    }).flat();
    leafs.map((it) => {
      this.onSelect(it.area);
    });
  }

  selectNone() {
    const room = this.defaultRoom?.rooms[0] ?? null;
    this._modal.dismiss(room);
  }

  onSelect(area: SiteArea) {
    if (this.isMultiple()) {
      area.selected = !area.selected;
      this.selected.update((value) => {
        if (area.selected) {
          value.push({ id: null, siteArea: area });
        } else {
          const index = value.findIndex((it) => it.id === area.guid);
          value.splice(index, 1);
        }
        return value;
      });
    } else {
      this._modal.dismiss(area);
    }
  }

  onArea(guid: string) {
    this.areas$.pipe(
      take(1),
    ).subscribe((areas) => {
      const area = areas.find((it) => it.guid === guid);
      this._modal.dismiss(area);
    });
  }

  selectAllRooms(area: SiteArea) {
    area.children.map((child) => {
      this.onSelect(child);
    });
  }

  onSelectMultiple() {
    this._modal.dismiss(this.selected());
  }

  setBlueprintImage(img: Event) {
    const imgElement = img.target as HTMLImageElement;
    imgElement.crossOrigin = 'anonymous';
    setTimeout(() => this.imageElement.set(imgElement), 300);
  }

  onBlueprint(event: IonSelectCustomEvent<SelectChangeEventDetail<SiteBlueprint>>) {
    this.imageElement.set(null);
    const blueprint = event.detail.value;
    this.selectedBlueprint.set(blueprint);
    const imageElement = new Image();
    imageElement.id = 'blueprint';
    imageElement.src = blueprint.url;
    imageElement.crossOrigin = 'anonymous';
    imageElement.className = 'canvas-img';
  }

  dismiss() {
    this._modal.dismiss();
  }
}
